import { FC, useState } from 'react';

import { Box, Divider, Flex, Text, IconButton, Button } from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';

import { Pickup, packages } from 'types/pickup';
import FormikInput from 'components/primitives/FormikInput';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import { Form, Formik } from 'formik';
import {
  calculateHarvestReportTime,
  HarvestReportWorkData,
} from 'utils/breakdownKpiCalculations';
import { usePickupKpiWithDateFormat } from 'hooks/usePickupKpi';
import {
  convertUTCToLocal,
  transformUTCStringToLocalDate,
} from 'utils/dateUtils';

export const Breakdown: FC<{
  pickup?: Pickup;
  onUpdate?: (payload: any) => Promise<any>;
  isUpdating?: boolean;
}> = ({ pickup, onUpdate, isUpdating = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const {
    updateWithDateFormat,
    isLoading: isUpdatingKpi,
  } = usePickupKpiWithDateFormat();

  // Date calculations are handled by harvestReportResult
  let employees = pickup?.employees ?? 0;

  const OpenedPackages = pickup?.pickedUpStats ?? [];
  const totalPackagesMap: { [sku: string]: any } = {};
  // Helper function to add or update package quantities in totalPackagesMap
  const addOrUpdatePackage = (pkg: packages, source: string) => {
    const { sku, quantity, ...rest } = pkg;
    if (!totalPackagesMap[sku] && sku !== undefined) {
      totalPackagesMap[sku] = {
        sku,
        openedQuantity: 0,
        sealedQuantity: 0,
        ...rest,
      };
    }
    if (source === 'opened' && sku !== undefined) {
      totalPackagesMap[sku].openedQuantity += quantity;
    } else if (source === 'sealed' && sku !== undefined) {
      totalPackagesMap[sku].sealedQuantity += quantity;
    }
  };

  // Process OpenedPackages
  OpenedPackages.forEach((pkg) => addOrUpdatePackage(pkg, 'opened'));

  // Convert totalPackagesMap to an array
  const totalPackages = Object.values(totalPackagesMap);
  const totalOpenedPackages = totalPackages.reduce(
    (acc, pkg) => acc + (isNaN(pkg.openedQuantity) ? 0 : pkg.openedQuantity),
    0,
  );

  // Use countingDate as the "Start Date" shown in UI for KPI calculations
  const startDate = new Date(pickup?.countingDate ?? pickup?.createdAt ?? '');

  const workData: HarvestReportWorkData = {
    startDate: startDate,
    numberOfEmployees: pickup?.employees,
    hours: Math.floor(pickup?.processTime?.hours ?? 0),
    minutes: Math.round(pickup?.processTime?.minutes ?? 0),
  };

  const harvestReportResult = calculateHarvestReportTime(
    workData,
    new Date(pickup?.date ?? pickup?.pickedUpDate ?? ''),
    totalOpenedPackages,
  );

  const handleSave = async (values: any) => {
    if (!pickup?._id) return;

    try {
      // Use the custom hook that handles all the complexity
      await updateWithDateFormat(pickup._id, {
        countingDate: values.countingDate,
        employees: values.employees,
        processTime: values.processTime,
      });

      setIsEditing(false);
    } catch (error) {
      // Error handling is already done in the hook
      console.error('Error in handleSave:', error);
    }
  };

  const getInitialValues = () => {
    // Convert countingDate to a format compatible with FormikDatepicker
    let initialDate = new Date();
    if (pickup?.countingDate) {
      // Use transformUTCStringToLocalDate to avoid timezone conversion issues
      initialDate = transformUTCStringToLocalDate(pickup.countingDate);
    }

    const initialValues = {
      countingDate: initialDate, // Keep as Date object for FormikDatepicker
      processTime: {
        hours: pickup?.processTime?.hours ?? 0,
        minutes: pickup?.processTime?.minutes ?? 0,
      },
      employees: pickup?.employees ?? 0,
    };

    return initialValues;
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      onSubmit={handleSave}
      enableReinitialize={!isEditing}>
      {({ values, setFieldValue, handleSubmit }) => (
        <Form>
          <Flex
            py="50px"
            direction="column"
            justify="center"
            align="center"
            fontWeight="600">
            <Flex direction="row" gap="40px" justify="center" align="center">
              <Flex
                direction="column"
                gap="20px"
                minW="350px"
                background="#F6F6F6"
                padding="20px"
                borderRadius="10px"
                position="relative">
                {/* Edit/Save Button */}
                <Box position="absolute" top="15px" right="15px">
                  {isEditing ? (
                    <Button
                      type="submit"
                      size="sm"
                      colorScheme="blue"
                      isLoading={isUpdatingKpi || isUpdating}>
                      Save
                    </Button>
                  ) : (
                    <IconButton
                      aria-label="Edit data"
                      icon={<MdEdit size="16px" />}
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsEditing(true)}
                    />
                  )}
                </Box>

                <Text fontSize="16px" fontWeight="600">
                  Data
                </Text>

                <Flex alignItems="center" width="100%">
                  <Box width="40%">
                    <Text textAlign="left" fontSize="14px" opacity={0.4} mr="4">
                      Start Date :
                    </Text>
                  </Box>
                  <Box width="60%">
                    <FormikDatepicker
                      labelProps={{
                        color: '#afafaf',
                      }}
                      disabled={!isEditing}
                      name="countingDate"
                      isEditable={isEditing}
                    />
                  </Box>
                </Flex>
                <Divider />
                <Text fontSize="14px" fontWeight="800" opacity={0.4}>
                  Process Time
                </Text>
                <Flex align="center" justify="space-between" gap="20px">
                  <Flex alignItems="center" width="100%" gap="20px">
                    <Box width="40%">
                      <Text
                        textAlign="right"
                        fontSize="14px"
                        opacity={0.4}
                        mr="4">
                        Hours:
                      </Text>
                    </Box>
                    <Box width="80%">
                      <FormikInput
                        name="processTime.hours"
                        isReadOnly={!isEditing}
                        containerProps={{ flex: 1 }}
                        width={'70%'}
                        type="number"
                        min={0}
                      />
                    </Box>
                  </Flex>
                  <Flex alignItems="center" width="100%" gap="20px">
                    <Box width="40%">
                      <Text
                        textAlign="right"
                        fontSize="14px"
                        opacity={0.4}
                        mr="2">
                        Minutes:
                      </Text>
                    </Box>
                    <Box width="100%">
                      <FormikInput
                        name="processTime.minutes"
                        isReadOnly={!isEditing}
                        containerProps={{ flex: 1 }}
                        width={'70%'}
                        type="number"
                        min={0}
                        max={59}
                      />
                    </Box>
                  </Flex>
                </Flex>
                <Divider />
                <Flex align="center" justify="space-between">
                  <Flex alignItems="center" width="100%">
                    <Box width="40%">
                      <Text
                        textAlign="right"
                        fontSize="14px"
                        opacity={0.4}
                        mr="4">
                        # of Employees :
                      </Text>
                    </Box>
                    <FormikInput
                      name="employees"
                      isReadOnly={!isEditing}
                      containerProps={{ flex: 1 }}
                      width={'50%'}
                      type="number"
                      min={0}
                    />
                  </Flex>
                </Flex>
              </Flex>

              <Flex
                direction="column"
                gap="40px"
                minW="350px"
                background="#F6F6F6"
                paddingX={'20px'}
                paddingY={'10px'}
                borderRadius="10px">
                <Text fontSize="16px" fontWeight="600">
                  Calculations
                </Text>
                <Flex align="center" justify="space-between" width={'100%'}>
                  <Flex alignItems="center" width="100%">
                    <Box width="60%">
                      <Text fontSize="14px" opacity={0.4} marginLeft="0">
                        Lead Time :{' '}
                      </Text>
                      <Text fontSize="12px" opacity={0.4}>
                        (Start Time - Pickup Date)
                      </Text>
                    </Box>
                    <FormikInput
                      name="leadTime"
                      isReadOnly
                      value={harvestReportResult.leadTime?.toString() ?? '0'}
                      containerProps={{ flex: 1 }}
                      width={'100%'}
                      marginBottom={2}
                    />
                  </Flex>
                </Flex>
                <Flex align="center" justify="space-between" width={'100%'}>
                  <Flex alignItems="center" width="100%">
                    <Box width="60%">
                      <Text fontSize="14px" opacity={0.4} marginLeft="0">
                        Labour Hours :{' '}
                      </Text>
                      <Text fontSize="12px" opacity={0.4}>
                        (Process Time X # of Employees)
                      </Text>
                    </Box>
                    <FormikInput
                      name="labourHours"
                      isReadOnly
                      value={
                        harvestReportResult.labourHours?.hours?.toFixed(2) ??
                        '0.00'
                      }
                      containerProps={{ flex: 1 }}
                      width={'100%'}
                      marginBottom={5}
                    />
                  </Flex>
                </Flex>

                <Flex align="center" justify="space-between" width={'100%'}>
                  <Flex alignItems="center" width="100%">
                    <Box width="60%">
                      <Text fontSize="14px" opacity={0.4} marginLeft="0">
                        Items Per Hour :{' '}
                      </Text>
                      <Text fontSize="12px" opacity={0.4}>
                        (Total Dirty / Labour Hours)
                      </Text>
                    </Box>
                    <FormikInput
                      name="itemsPerHour"
                      isReadOnly
                      value={
                        harvestReportResult.itemsPerHour?.toFixed(2) ?? '0.00'
                      }
                      containerProps={{ flex: 1 }}
                      width={'100%'}
                      marginBottom={5}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};
